// ActivityDailyDigest.tsx — STEP-PWA-ACTIVITY-DAILY-DIGEST
//
// Expanded daily digest for the Activity tab.
// - Shows current 24h window with DELTA framing vs. prior period.
// - 8h refresh cadence; displays generated_at timestamp and window.
// - STALE badge when generated_at > 10h ago.
// - UNAVAILABLE on fetch failure — never silent.
// - Empty sections render as explicit "No X this period" text.
// - TrendSparkline for PR and step trends where history exists.

import { useCallback, useEffect, useState } from "react";
import { api } from "../../api/client.js";
import type { DailyDigestResponse, DigestBucket, DigestDelta } from "../../api/types.js";
import { TrendSparkline } from "../../components/TrendSparkline.js";
import { useTimeShift } from "../TimeShift.js";

const REFRESH_INTERVAL_MS = 8 * 60 * 60 * 1000; // 8h
const STALE_THRESHOLD_MS = 10 * 60 * 60 * 1000; // 10h

// ─── Formatting helpers ─────────────────────────────────────────────────────

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " + fmtTime(iso);
  } catch {
    return iso;
  }
}

function deltaLabel(n: number): { text: string; tone: "up" | "down" | "flat" } {
  if (n === 0) return { text: "—", tone: "flat" };
  return n > 0
    ? { text: `+${n} vs prior`, tone: "up" }
    : { text: `${n} vs prior`, tone: "down" };
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: "var(--text-dim)",
        marginBottom: 6,
        marginTop: 16,
      }}
    >
      {children}
    </div>
  );
}

function DeltaBadge({ n, invertDown = false }: { n: number; invertDown?: boolean }) {
  const { text, tone } = deltaLabel(n);
  const color =
    tone === "flat"
      ? "var(--text-dim)"
      : tone === "up"
        ? invertDown
          ? "var(--color-alert, #e05252)"
          : "var(--color-ok, #4caf50)"
        : invertDown
          ? "var(--color-ok, #4caf50)"
          : "var(--color-alert, #e05252)";
  return (
    <span style={{ fontSize: 10, color, marginLeft: 6 }}>{text}</span>
  );
}

function MergesSection({ bucket, delta }: { bucket: DigestBucket; delta: DigestDelta }) {
  return (
    <div>
      <SectionLabel>Merges this period</SectionLabel>
      {bucket.prs_merged === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No merges this period</div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 20, fontWeight: 600, fontFamily: "var(--font-mono)" }}>
            {bucket.prs_merged}
          </span>
          <span style={{ fontSize: 11, color: "var(--text-muted)" }}>PRs merged</span>
          <DeltaBadge n={delta.prs_merged_delta} />
        </div>
      )}
      {delta.prs_merged_trend.length >= 2 && (
        <div style={{ marginTop: 6, width: 120 }}>
          <TrendSparkline
            series={delta.prs_merged_trend}
            height={28}
            color="var(--color-ok, #4caf50)"
          />
        </div>
      )}
    </div>
  );
}

function StepsSection({ bucket, delta }: { bucket: DigestBucket; delta: DigestDelta }) {
  return (
    <div>
      <SectionLabel>Steps completed</SectionLabel>
      {bucket.steps_completed === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No steps completed this period</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: 20, fontWeight: 600, fontFamily: "var(--font-mono)" }}>
              {bucket.steps_completed}
            </span>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>steps</span>
            <DeltaBadge n={delta.steps_completed_delta} />
          </div>
          {delta.steps_completed_trend.length >= 2 && (
            <div style={{ marginTop: 6, width: 120 }}>
              <TrendSparkline
                series={delta.steps_completed_trend}
                height={28}
                color="var(--color-ok, #4caf50)"
              />
            </div>
          )}
          {bucket.notable_steps.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {bucket.notable_steps.map((s) => (
                <div
                  key={s.id}
                  style={{ fontSize: 11, color: "var(--text-muted)", padding: "1px 0" }}
                >
                  <span style={{ fontFamily: "var(--font-mono)", color: "var(--text)" }}>
                    {s.id}
                  </span>{" "}
                  — {s.title}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function FailuresSection({ bucket, delta }: { bucket: DigestBucket; delta: DigestDelta }) {
  return (
    <div>
      <SectionLabel>Failed dispatches</SectionLabel>
      {bucket.dispatches_failed === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No failures this period</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 20,
                fontWeight: 600,
                fontFamily: "var(--font-mono)",
                color: "var(--color-alert, #e05252)",
              }}
            >
              {bucket.dispatches_failed}
            </span>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>failed</span>
            <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
              of {bucket.dispatches_run} run
            </span>
            <DeltaBadge n={delta.dispatches_failed_delta} invertDown />
          </div>
          {bucket.failed_jobs.length > 0 && (
            <div style={{ marginTop: 6 }}>
              {bucket.failed_jobs.map((j) => (
                <div
                  key={j.job_id}
                  style={{
                    fontSize: 11,
                    color: "var(--text-muted)",
                    padding: "2px 0",
                    borderLeft: "2px solid var(--color-alert, #e05252)",
                    paddingLeft: 6,
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontFamily: "var(--font-mono)" }}>{j.job_id}</span>
                  {j.persona_id && (
                    <span style={{ color: "var(--text-dim)", marginLeft: 4 }}>
                      [{j.persona_id}]
                    </span>
                  )}
                  {j.failure_summary && (
                    <div style={{ color: "var(--text-dim)", marginTop: 2 }}>
                      {j.failure_summary}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BlockedSection({ bucket, delta }: { bucket: DigestBucket; delta: DigestDelta }) {
  return (
    <div>
      <SectionLabel>Newly blocked steps</SectionLabel>
      {bucket.newly_blocked_steps.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>No newly blocked steps</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: 20,
                fontWeight: 600,
                fontFamily: "var(--font-mono)",
                color: "var(--color-warn, #f0a830)",
              }}
            >
              {bucket.newly_blocked_steps.length}
            </span>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>blocked</span>
            <DeltaBadge n={delta.newly_blocked_delta} invertDown />
          </div>
          <div style={{ marginTop: 6 }}>
            {bucket.newly_blocked_steps.map((s) => (
              <div
                key={s.id}
                style={{ fontSize: 11, color: "var(--text-muted)", padding: "1px 0" }}
              >
                <span style={{ fontFamily: "var(--font-mono)", color: "var(--text)" }}>
                  {s.id}
                </span>{" "}
                — {s.title}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function CostSection({ bucket, delta }: { bucket: DigestBucket; delta: DigestDelta }) {
  const cost = bucket.total_cost_usd;
  const priorCost = bucket.total_cost_usd - delta.cost_usd_delta;
  const pctChange =
    priorCost > 0 ? ((cost - priorCost) / priorCost) * 100 : null;

  return (
    <div>
      <SectionLabel>Cost &amp; throughput</SectionLabel>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
        <div>
          <div
            style={{ fontSize: 20, fontWeight: 600, fontFamily: "var(--font-mono)" }}
          >
            ${cost.toFixed(2)}
          </div>
          <div style={{ fontSize: 10, color: "var(--text-dim)" }}>
            cost this period
            {pctChange !== null && (
              <span
                style={{
                  marginLeft: 4,
                  color:
                    pctChange > 0
                      ? "var(--color-alert, #e05252)"
                      : "var(--color-ok, #4caf50)",
                }}
              >
                {pctChange > 0 ? "+" : ""}
                {pctChange.toFixed(0)}%
              </span>
            )}
          </div>
        </div>
        <div>
          <div
            style={{ fontSize: 20, fontWeight: 600, fontFamily: "var(--font-mono)" }}
          >
            {bucket.dispatches_succeeded}
          </div>
          <div style={{ fontSize: 10, color: "var(--text-dim)" }}>
            successful dispatches
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function ActivityDailyDigest() {
  const { asOf, isLive } = useTimeShift();
  const [digest, setDigest] = useState<DailyDigestResponse | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [fetchError, setFetchError] = useState<unknown>(null);

  const load = useCallback(async () => {
    try {
      const data = await api.dailyDigest(asOf);
      setDigest(data);
      setFetchError(null);
    } catch (err) {
      setFetchError(err);
    } finally {
      setLoaded(true);
    }
  }, [asOf]);

  useEffect(() => {
    void load();
    if (!isLive) return;
    const interval = setInterval(() => void load(), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load, isLive]);

  // ─── Loading state ─────────────────────────────────────────────────────────
  if (!loaded && !fetchError) {
    return (
      <div
        style={{
          padding: "16px 20px",
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--text-dim)",
        }}
      >
        Loading daily digest…
      </div>
    );
  }

  // ─── Unavailable state ─────────────────────────────────────────────────────
  if (fetchError || !digest) {
    return (
      <div
        style={{
          padding: "12px 20px",
          background: "var(--bg-surface, #1a1a1a)",
          border: "1px solid var(--border-soft)",
          borderRadius: 4,
          margin: "12px 20px",
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--color-alert, #e05252)",
            fontWeight: 600,
          }}
        >
          ● UNAVAILABLE
        </div>
        <div style={{ fontSize: 11, color: "var(--text-dim)", marginTop: 4 }}>
          Daily digest could not be loaded. Pull-to-refresh or wait for next automatic refresh.
        </div>
      </div>
    );
  }

  // ─── Stale check (generated_at > 10h ago) ─────────────────────────────────
  const generatedAtMs = new Date(digest.generated_at).getTime();
  const isStale = digest.stale || Date.now() - generatedAtMs > STALE_THRESHOLD_MS;

  return (
    <div style={{ padding: "0 20px 24px" }}>
      {/* Header: timestamp + window + optional stale badge */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 4,
          marginTop: 12,
        }}
      >
        <div
          style={{
            fontSize: 10,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            color: "var(--text-dim)",
          }}
        >
          Daily digest · generated {fmtDate(digest.generated_at)}
        </div>
        {isStale && (
          <div
            style={{
              fontSize: 10,
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              color: "var(--color-warn, #f0a830)",
              background: "rgba(240, 168, 48, 0.12)",
              border: "1px solid var(--color-warn, #f0a830)",
              borderRadius: 3,
              padding: "1px 6px",
              letterSpacing: 0.5,
            }}
          >
            STALE
          </div>
        )}
      </div>
      <div
        style={{
          fontSize: 10,
          color: "var(--text-dim)",
          marginBottom: 16,
          borderBottom: "1px solid var(--border-soft)",
          paddingBottom: 10,
        }}
      >
        {isLive ? "Window: " : "Scrubbed window: "}
        {fmtDate(digest.window_start)} → {fmtDate(digest.window_end)}
        <span style={{ marginLeft: 8, color: "var(--text-dim)" }}>
          (vs. prior 24h)
        </span>
      </div>

      {/* Digest sections */}
      <MergesSection bucket={digest.current} delta={digest.delta} />
      <StepsSection bucket={digest.current} delta={digest.delta} />
      <FailuresSection bucket={digest.current} delta={digest.delta} />
      <BlockedSection bucket={digest.current} delta={digest.delta} />
      <CostSection bucket={digest.current} delta={digest.delta} />
    </div>
  );
}
